/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  TextField,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useSnackbar } from "notistack";
import { useMutation } from "@apollo/client";
import GENERATE_TTS_AUDIO from "src/graphql/mutation/chat/generateTtsAudio";
import {
  getCallSettings,
  updateCallSettings,
  uploadVoicemailViaBackend,
} from "./whatsappDetailFunc";

// Weekly hours editor uses day-of-week enums Meta expects.
const DAYS = [
  { key: "MONDAY", label: "Mon" },
  { key: "TUESDAY", label: "Tue" },
  { key: "WEDNESDAY", label: "Wed" },
  { key: "THURSDAY", label: "Thu" },
  { key: "FRIDAY", label: "Fri" },
  { key: "SATURDAY", label: "Sat" },
  { key: "SUNDAY", label: "Sun" },
];

// Small curated timezone list — extend if agents in more regions need it.
const TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Istanbul",
  "Africa/Cairo",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

// TTS voice presets (must match backend SUPPORTED_VOICES in textToSpeech.js).
const TTS_VOICES = [
  { id: "alloy", label: "Alloy" },
  { id: "coral", label: "Coral" },
  { id: "nova", label: "Nova" },
  { id: "onyx", label: "Onyx" },
  { id: "shimmer", label: "Shimmer" },
];

// Convert "HH:MM" (from <input type=time>) to Meta's "HHMM" 4-digit format.
const hhmmFromInput = (v: string) => (v || "").replace(":", "");
// And the reverse: "HHMM" -> "HH:MM"
const hhmmToInput = (v: string) => {
  if (!v || v.length !== 4) return "";
  return `${v.slice(0, 2)}:${v.slice(2)}`;
};

// Base64 audio string → File object (from the TTS response).
const base64ToBlob = (base64: string, mimetype: string): Blob => {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    bytes[i] = byteString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimetype });
};

interface HourRange {
  day_of_week: string;
  open_time: string; // "HHMM"
  close_time: string; // "HHMM"
}

interface Props {
  open: boolean;
  onClose: () => void;
  phoneNumberId: string;
  businessToken: string;
  displayName?: string;
  waNumber?: string;
}

const CallSettingsModal: React.FC<Props> = ({
  open,
  onClose,
  phoneNumberId,
  businessToken,
  displayName,
  waNumber,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // ---- General state ----
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- Voicemail state ----
  const [vmEnabled, setVmEnabled] = useState(false);
  const [vmMode, setVmMode] = useState<"upload" | "record" | "tts">("upload");
  // Prepared media that will be uploaded on Save:
  const [vmPreparedBlob, setVmPreparedBlob] = useState<Blob | null>(null);
  const [vmPreparedFilename, setVmPreparedFilename] = useState<string>("");
  // (mimetype not tracked in state — backend ffmpeg detects format from
  // file contents regardless of what the browser thinks it is)
  const [vmPreparedPreviewUrl, setVmPreparedPreviewUrl] = useState<string>("");
  // Already-set media on Meta (loaded from GET). Only its ID; no preview URL.
  const [vmExistingMediaId, setVmExistingMediaId] = useState<string | null>(
    null,
  );

  // Recorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const recordedChunksRef = useRef<Blob[]>([]);

  // TTS
  const [ttsText, setTtsText] = useState("");
  const [ttsVoice, setTtsVoice] = useState("nova");
  const [generateTtsAudio, { loading: ttsLoading }] =
    useMutation(GENERATE_TTS_AUDIO);

  // ---- Call hours state ----
  const [chEnabled, setChEnabled] = useState(false);
  const [chTimezone, setChTimezone] = useState("Asia/Karachi");
  // One entry per day (extendable to two later if needed).
  const [chRanges, setChRanges] = useState<HourRange[]>(
    DAYS.map((d) => ({
      day_of_week: d.key,
      open_time: "",
      close_time: "",
    })),
  );

  // ---- Load existing settings when the modal opens ----
  useEffect(() => {
    let cancelled = false;
    if (open) {
      (async () => {
        try {
          setLoading(true);
          const data = await getCallSettings(phoneNumberId, businessToken);
          if (cancelled) return;
          const calling = data?.calling || {};

          // Voicemail
          const vm = calling.voicemail || {};
          setVmEnabled(vm.status === "ENABLED");
          const existingMediaId =
            vm?.audio?.default?.announcement_media_id?.toString() || null;
          setVmExistingMediaId(existingMediaId);

          // Call hours
          const ch = calling.call_hours || {};
          setChEnabled(ch.status === "ENABLED");
          if (ch.timezone_id) setChTimezone(ch.timezone_id);
          const loadedRanges: HourRange[] = DAYS.map((d) => {
            const found = (ch.weekly_operating_hours || []).find(
              (r: any) => r.day_of_week === d.key,
            );
            return {
              day_of_week: d.key,
              open_time: found?.open_time || "",
              close_time: found?.close_time || "",
            };
          });
          setChRanges(loadedRanges);
        } catch (err: any) {
          enqueueSnackbar(
            `Could not load call settings: ${err?.message || err}`,
            { variant: "error" },
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- Voicemail: file upload handler ----
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Any audio format is fine — the backend will ffmpeg-convert to
    // OGG/OPUS before forwarding to Meta. We just make sure it's audio.
    if (file.type && !file.type.startsWith("audio/")) {
      enqueueSnackbar("Please choose an audio file.", {
        variant: "warning",
      });
      return;
    }
    setVmPreparedBlob(file);
    setVmPreparedFilename(file.name);
    setVmPreparedPreviewUrl(URL.createObjectURL(file));
  };

  // ---- Voicemail: mic recording ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordedChunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) recordedChunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: "audio/webm",
        });
        setVmPreparedBlob(blob);
        setVmPreparedFilename(`voicemail-${Date.now()}.webm`);
        setVmPreparedPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setIsRecording(true);
    } catch (err) {
      enqueueSnackbar(
        "Microphone access denied. Please allow mic in browser permissions.",
        { variant: "error" },
      );
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  // ---- Voicemail: TTS ----
  const handleGenerateTts = async () => {
    if (!ttsText.trim()) {
      enqueueSnackbar("Enter some text first.", { variant: "warning" });
      return;
    }
    try {
      const res = await generateTtsAudio({
        variables: { text: ttsText.trim(), voice: ttsVoice, toneId: "neutral" },
      });
      const g = res.data?.generateTtsAudio;
      if (!g?.audioBase64) throw new Error("No audio returned");
      const blob = base64ToBlob(g.audioBase64, g.mimetype || "audio/ogg");
      setVmPreparedBlob(blob);
      setVmPreparedFilename(g.filename || `voicemail-${Date.now()}.ogg`);
      setVmPreparedPreviewUrl(URL.createObjectURL(blob));
      enqueueSnackbar("Voice generated. Preview above.", {
        variant: "success",
      });
    } catch (err: any) {
      enqueueSnackbar(`TTS failed: ${err?.message || err}`, {
        variant: "error",
      });
    }
  };

  // ---- Save handler ----
  const handleSave = async () => {
    // Basic validation on hours if enabled
    if (chEnabled) {
      const bad = chRanges.filter(
        (r) =>
          (r.open_time && !r.close_time) ||
          (!r.open_time && r.close_time) ||
          (r.open_time && r.close_time && r.open_time >= r.close_time),
      );
      if (bad.length > 0) {
        enqueueSnackbar(
          "Fix hour ranges: open must be before close, both filled or both empty.",
          { variant: "warning" },
        );
        return;
      }
    }

    setSaving(true);
    try {
      const calling: Record<string, any> = {};

      // --- Voicemail ---
      if (vmEnabled) {
        let mediaId = vmExistingMediaId;
        // Upload new media if the admin prepared something.
        // The `call_voicemail_announcement` use_case is required — a
        // regular messaging media_id is rejected by /settings.
        if (vmPreparedBlob) {
          // Route through our backend: it ffmpeg-normalizes whatever
          // the user provided (any audio format) into OGG/OPUS, then
          // forwards to Meta with the correct use_case tag. Business
          // token stays server-side.
          mediaId = await uploadVoicemailViaBackend(
            phoneNumberId,
            vmPreparedBlob,
            vmPreparedFilename || `voicemail-${Date.now()}`,
          );
          setVmExistingMediaId(mediaId);
        }
        if (!mediaId) {
          throw new Error(
            "Voicemail is enabled but no audio has been provided. Upload, record, or generate one.",
          );
        }
        calling.voicemail = {
          status: "ENABLED",
          triggers: ["TIMEOUT"],
          audio: {
            default: {
              // Meta expects an integer, not a string.
              announcement_media_id: Number(mediaId),
              // Meta's valid range is 0–30 inclusive. 30 = longest wait
              // before voicemail kicks in.
              timeout_seconds: 30,
            },
          },
        };
      } else if (vmExistingMediaId) {
        // Voicemail OFF but Meta previously had it enabled — actively
        // disable it so the customer stops hearing the greeting. Meta's
        // schema requires the full shape (triggers + audio) even when
        // status is DISABLED, so we preserve the existing media_id as
        // filler; Meta ignores the audio because status=DISABLED.
        calling.voicemail = {
          status: "DISABLED",
          triggers: ["TIMEOUT"],
          audio: {
            default: {
              announcement_media_id: Number(vmExistingMediaId),
              timeout_seconds: 30,
            },
          },
        };
      }
      // If no prior media_id exists (voicemail never enabled), skip the
      // voicemail key entirely. Meta's default state is OFF, nothing to
      // change.

      // --- Call hours ---
      if (chEnabled) {
        calling.call_hours = {
          status: "ENABLED",
          timezone_id: chTimezone,
          weekly_operating_hours: chRanges
            .filter((r) => r.open_time && r.close_time)
            .map((r) => ({
              day_of_week: r.day_of_week,
              open_time: r.open_time,
              close_time: r.close_time,
            })),
        };
      } else {
        // Toggle OFF means "available 24/7". Meta's DISABLED shape gets
        // rejected because the schema demands a non-empty
        // weekly_operating_hours. So we send an ENABLED schedule that
        // covers every day 00:00–23:59 — functionally identical to 24/7
        // availability and accepted by the schema without complaint.
        calling.call_hours = {
          status: "ENABLED",
          timezone_id: chTimezone,
          weekly_operating_hours: DAYS.map((d) => ({
            day_of_week: d.key,
            open_time: "0000",
            close_time: "2359",
          })),
        };
      }

      await updateCallSettings(phoneNumberId, businessToken, calling);
      enqueueSnackbar("Call settings saved.", { variant: "success" });
      onClose();
    } catch (err: any) {
      enqueueSnackbar(`Save failed: ${err?.message || err}`, {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>Call Settings</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {displayName ? `${displayName} · ` : ""}
            {waNumber || phoneNumberId}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* --- Voicemail section --- */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Voicemail</Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {vmEnabled
                      ? "Play an audio message when a call isn't answered within 30s"
                      : "Off: no voicemail plays — calls simply ring out with no announcement"}
                  </Typography>
                </Box>
                <Switch
                  checked={vmEnabled}
                  onChange={(e) => setVmEnabled(e.target.checked)}
                />
              </Box>

              {vmEnabled && (
                <Box sx={{ mt: 2 }}>
                  {vmExistingMediaId && !vmPreparedBlob && (
                    <Chip
                      size="small"
                      label={`Current media id: ${vmExistingMediaId}`}
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Tabs
                    value={vmMode}
                    onChange={(_, v) => setVmMode(v)}
                    variant="fullWidth"
                    sx={{ minHeight: 36 }}
                  >
                    <Tab
                      value="upload"
                      label="Upload"
                      icon={<UploadFileIcon fontSize="small" />}
                      iconPosition="start"
                      sx={{ minHeight: 36 }}
                    />
                    <Tab
                      value="record"
                      label="Record"
                      icon={<MicIcon fontSize="small" />}
                      iconPosition="start"
                      sx={{ minHeight: 36 }}
                    />
                    <Tab
                      value="tts"
                      label="Text-to-voice"
                      icon={<RecordVoiceOverIcon fontSize="small" />}
                      iconPosition="start"
                      sx={{ minHeight: 36 }}
                    />
                  </Tabs>

                  <Box sx={{ mt: 2 }}>
                    {vmMode === "upload" && (
                      <Box>
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={handleFileSelect}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1, color: "text.secondary" }}
                        >
                          Any audio file — MP3, WAV, M4A, OGG. We convert it to
                          the format WhatsApp needs before uploading.
                        </Typography>
                      </Box>
                    )}

                    {vmMode === "record" && (
                      <Box>
                        {!isRecording ? (
                          <Button
                            variant="outlined"
                            startIcon={<MicIcon />}
                            onClick={startRecording}
                          >
                            Start recording
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<StopIcon />}
                            onClick={stopRecording}
                          >
                            Stop recording
                          </Button>
                        )}
                      </Box>
                    )}

                    {vmMode === "tts" && (
                      <Box>
                        <TextField
                          multiline
                          minRows={3}
                          fullWidth
                          placeholder="Type the voicemail announcement here…"
                          value={ttsText}
                          onChange={(e) => setTtsText(e.target.value)}
                          inputProps={{ maxLength: 1000 }}
                        />
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <Select
                            size="small"
                            value={ttsVoice}
                            onChange={(e) => setTtsVoice(e.target.value)}
                          >
                            {TTS_VOICES.map((v) => (
                              <MenuItem key={v.id} value={v.id}>
                                {v.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Button
                            variant="outlined"
                            onClick={handleGenerateTts}
                            disabled={ttsLoading || !ttsText.trim()}
                            startIcon={
                              ttsLoading ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PlayArrowIcon />
                              )
                            }
                          >
                            {ttsLoading ? "Generating…" : "Generate voice"}
                          </Button>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", ml: "auto" }}
                          >
                            {ttsText.length}/1000
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {vmPreparedPreviewUrl && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Preview (this will upload on Save):
                        </Typography>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                        <audio
                          src={vmPreparedPreviewUrl}
                          controls
                          style={{ width: "100%" }}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: "text.secondary" }}
                        >
                          {vmPreparedFilename}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* --- Call hours section --- */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Call hours</Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {chEnabled
                      ? "Only accept calls during selected days and times"
                      : "Off: number is available for calls 24/7"}
                  </Typography>
                </Box>
                <Switch
                  checked={chEnabled}
                  onChange={(e) => setChEnabled(e.target.checked)}
                />
              </Box>

              {chEnabled && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Timezone
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={chTimezone}
                      onChange={(e) => setChTimezone(e.target.value)}
                    >
                      {TIMEZONES.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Days & hours (leave both empty to skip that day)
                  </Typography>

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {chRanges.map((r, i) => (
                      <Box
                        key={r.day_of_week}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{ width: 40, fontSize: 13, fontWeight: 500 }}
                        >
                          {DAYS[i].label}
                        </Typography>
                        <TextField
                          type="time"
                          size="small"
                          value={hhmmToInput(r.open_time)}
                          onChange={(e) => {
                            const val = hhmmFromInput(e.target.value);
                            setChRanges((prev) =>
                              prev.map((row, idx) =>
                                idx === i ? { ...row, open_time: val } : row,
                              ),
                            );
                          }}
                          sx={{ width: 140 }}
                        />
                        <Typography sx={{ fontSize: 13 }}>to</Typography>
                        <TextField
                          type="time"
                          size="small"
                          value={hhmmToInput(r.close_time)}
                          onChange={(e) => {
                            const val = hhmmFromInput(e.target.value);
                            setChRanges((prev) =>
                              prev.map((row, idx) =>
                                idx === i ? { ...row, close_time: val } : row,
                              ),
                            );
                          }}
                          sx={{ width: 140 }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallSettingsModal;
